/**
 * useUserPref — серверное хранилище UI-prefs.
 *
 * Заменяет localStorage. Все prefs хранятся на сервере конкретного
 * клиента (panel.db, таблица users, колонка prefs JSON), поэтому:
 *   - переживают чистку кэша браузера
 *   - синхронизируются между несколькими вкладками/устройствами
 *   - не утекают в чужие сессии (привязаны к user_id)
 *
 * Использование:
 *   const [theme, setTheme] = useUserPref('cb-theme', 'dark')
 *
 * Архитектура:
 *   1. Контекст PrefsProvider загружает все prefs одним запросом /api/me
 *      (поле "prefs" в ответе) при mount приложения.
 *   2. setX(value) обновляет локальный кэш моментально + ставит в очередь
 *      PUT /api/me/prefs со 500ms debounce (в очереди — патч из всех
 *      изменений за окно).
 *   3. Если bootstrap ещё идёт — возвращается defaultValue, после загрузки
 *      компоненты re-render'ятся.
 *   4. Миграция legacy: если в localStorage есть значение и его нет в
 *      серверных prefs — копируем при первой загрузке (одноразово).
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState, createContext, type ReactNode } from 'react'

type PrefsState = {
  prefs: Record<string, unknown>
  loaded: boolean
  setPref: (key: string, value: unknown) => void
}

const PrefsContext = createContext<PrefsState | null>(null)

const _DEBOUNCE_MS = 500

export function PrefsProvider({ children, initial }: { children: ReactNode; initial?: Record<string, unknown> }) {
  const [prefs, setPrefs] = useState<Record<string, unknown>>(initial || {})
  const [loaded, setLoaded] = useState<boolean>(!!initial)
  const pendingRef = useRef<Record<string, unknown>>({})
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flushingRef = useRef(false)

  // Initial fetch: полный объект prefs c сервера.
  useEffect(() => {
    if (initial) return
    let cancelled = false
    void (async () => {
      try {
        const r = await fetch('/api/me/prefs')
        if (!r.ok) {
          setLoaded(true)
          return
        }
        const data = await r.json()
        if (cancelled) return
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          setPrefs(data)
        }
      } catch { /* ignore */ }
      finally {
        if (!cancelled) setLoaded(true)
      }
    })()
    return () => { cancelled = true }
  }, [initial])

  // Migration: при первой загрузке копируем все legacy localStorage
  // prefs (cb-*, sidebar_collapsed) в server, после чего удаляем.
  useEffect(() => {
    if (!loaded) return
    try {
      const _LEGACY_KEYS = [
        'cb-panel-mode', 'cb-theme', 'cb-style', 'cb-active-project',
        'cb-project-filter', 'cb-server-name', 'cb-bottom-panel',
        'cb-booster-right-tab', 'sidebar_collapsed',
        'ideas-last-seen-ts', 'bugs-last-seen-ts',
      ]
      const patch: Record<string, unknown> = {}
      for (const k of _LEGACY_KEYS) {
        const v = localStorage.getItem(k)
        if (v != null && prefs[k] === undefined) {
          // Try parse JSON, fallback to raw string.
          try { patch[k] = JSON.parse(v) } catch { patch[k] = v }
        }
      }
      if (Object.keys(patch).length > 0) {
        setPrefs(p => ({ ...p, ...patch }))
        Object.assign(pendingRef.current, patch)
        _scheduleFlush()
        for (const k of _LEGACY_KEYS) localStorage.removeItem(k)
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded])

  const _flush = useCallback(async () => {
    if (flushingRef.current) return
    const patch = pendingRef.current
    if (Object.keys(patch).length === 0) return
    pendingRef.current = {}
    flushingRef.current = true
    try {
      await fetch('/api/me/prefs', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      })
    } catch { /* ignore — value уже в local cache */ }
    finally {
      flushingRef.current = false
      // Если за время flush накопились новые изменения — рекурсивно flush.
      if (Object.keys(pendingRef.current).length > 0) {
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => { void _flush() }, _DEBOUNCE_MS)
      }
    }
  }, [])

  const _scheduleFlush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => { void _flush() }, _DEBOUNCE_MS)
  }, [_flush])

  const setPref = useCallback((key: string, value: unknown) => {
    setPrefs(p => {
      if (p[key] === value) return p
      return { ...p, [key]: value }
    })
    pendingRef.current[key] = value
    _scheduleFlush()
  }, [_scheduleFlush])

  // Flush on unmount/page hide.
  useEffect(() => {
    const onHide = () => {
      if (Object.keys(pendingRef.current).length === 0) return
      try {
        const blob = new Blob([JSON.stringify(pendingRef.current)], { type: 'application/json' })
        // sendBeacon переживает unload, в отличие от fetch
        navigator.sendBeacon?.('/api/me/prefs', blob)
        pendingRef.current = {}
      } catch { /* ignore */ }
    }
    window.addEventListener('pagehide', onHide)
    return () => window.removeEventListener('pagehide', onHide)
  }, [])

  const value = useMemo(() => ({ prefs, loaded, setPref }), [prefs, loaded, setPref])
  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>
}

/**
 * useUserPref(key, defaultValue) — `useState`-подобный API над серверным prefs.
 * До загрузки prefs возвращает defaultValue. Запись — через debounce 500ms.
 */
export function useUserPref<T>(key: string, defaultValue: T): [T, (v: T | ((cur: T) => T)) => void] {
  const ctx = useContext(PrefsContext)
  if (!ctx) {
    // Standalone fallback (без Provider) — просто local state, не персистится.
    const [v, setV] = useState<T>(defaultValue)
    return [v, setV as any]
  }
  const raw = ctx.prefs[key]
  const value = (raw === undefined ? defaultValue : raw) as T
  const setter = useCallback((v: T | ((cur: T) => T)) => {
    const next = typeof v === 'function'
      ? (v as (cur: T) => T)(value)
      : v
    ctx.setPref(key, next)
  }, [ctx, key, value])
  return [value, setter]
}

export function usePrefsLoaded(): boolean {
  const ctx = useContext(PrefsContext)
  return ctx?.loaded ?? true
}
